import React, { useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { InView } from 'react-intersection-observer';

import {
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';

import useSimilarBreedsSeeAll from '../../hooks/useSimilarBreedsSeeAll';
import { sendResultGroupViewedEvent } from '../../utils/resultGroupViewedEvent';
import { SIMILAR_BREEDS_RESULTS_TYPES } from '../../utils/similarBreeds';
import SimilarBreedsSeeAllOverlay from './see_all/SimilarBreedsSeeAllOverlay';
import SimilarBreedsPuppyCarousel from './SimilarBreedsPuppyCarousel';
import SimilarBreedsSectionFooter from './SimilarBreedsSectionFooter';

interface SimilarBreedsModuleProps {
  /** The feed the module is anchored in - it renders in both. */
  view: string;
  puppies: any[];
  /** The module's own filters, handed to the overlay unchanged. */
  filters: Record<string, unknown>;
  className?: string | null;
}

// The module is anchored on page one and nowhere else.
const MODULE_PAGE_NUMBER = 1;

/** The similar breeds carousel, with its "See all" overlay. */
export default function SimilarBreedsModule({
  view,
  puppies,
  filters,
  className = null
}: SimilarBreedsModuleProps) {
  const seeAll = useSimilarBreedsSeeAll();
  const queryClient = useQueryClient();
  const breedId = useSelector(({ server }: any) => server.breedId);

  // One event for the whole module rather than the feed's groups of four: it is
  // a fixed handful of cards shown together, with no second group to scroll into.
  const trackInView = useCallback(
    (inView: boolean) => {
      if (!inView || !puppies.length) {
        return;
      }

      sendResultGroupViewedEvent(queryClient, {
        breedId,
        resultsType: SIMILAR_BREEDS_RESULTS_TYPES.PUPPIES,
        resultGroup: puppies,
        position: 1,
        page: MODULE_PAGE_NUMBER,
        view,
        viewFeature: ANALYTICS_VIEW_FEATURES.PUPPY_CARD,
        viewFeatureLocation:
          ANALYTICS_VIEW_FEATURE_LOCATIONS.SIMILAR_BREED_PUPPIES,
        // These rows belong to breeds other than the page's, and the module
        // runs on every eligible search - see `sendResultGroupViewedEvent`.
        fetchAvailability: false
      });
    },
    [queryClient, breedId, puppies, view]
  );

  // Closing the overlay is not a navigation, so without this a contact made
  // from the feed afterwards would still be attributed to the overlay. Keyed to
  // the overlay closing rather than unmounting, so clicking through to a profile
  // keeps the origin it just set.
  const wasOverlayOpenRef = useRef(false);

  useEffect(() => {
    if (seeAll.isOpen) {
      wasOverlayOpenRef.current = true;
      return;
    }

    if (!wasOverlayOpenRef.current) {
      return;
    }

    wasOverlayOpenRef.current = false;
    setAnalyticsOrigin({ originView: view });
  }, [seeAll.isOpen, view]);

  // No click event of its own, per the tracking spec: the `Breed Searched` the
  // overlay fires is what records the engagement.
  function handleViewAllClick() {
    seeAll.open(SIMILAR_BREEDS_RESULTS_TYPES.PUPPIES);
  }

  const overlay = seeAll.isOpen ? (
    <SimilarBreedsSeeAllOverlay filters={filters} />
  ) : null;

  return (
    <>
      {/* No `threshold`, unlike the feed's cards at 0.8: a carousel that never
          comes fully into view at once would drop the impression entirely. */}
      <InView triggerOnce onChange={trackInView}>
        <SimilarBreedsPuppyCarousel
          className={className}
          puppies={puppies}
          view={view}
          footer={
            <SimilarBreedsSectionFooter onViewAllClick={handleViewAllClick} />
          }
        />
      </InView>
      {overlay}
    </>
  );
}
